"use client";
import React, { useState, useRef } from 'react';

interface CardCarouselProps {
  children: React.ReactNode[];
  className?: string;
}

export const CardCarousel: React.FC<CardCarouselProps> = ({
  children,
  className = '',
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const totalCards = children.length;

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + totalCards) % totalCards);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % totalCards);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrevious();
    }
  };


  return (
    <div className={`relative ${className}`}>
      {/* Mobile Carousel View */}
      <div className="block sm:hidden">
        <div
          ref={containerRef}
          className="relative overflow-hidden"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div
            className="flex transition-transform duration-300 ease-in-out"
            style={{
              transform: `translateX(-${currentIndex * 100}%)`,
            }}
          >
            {children.map((child, index) => (
              <div
                key={index}
                className="w-full flex-shrink-0 px-5"
              >
                {child}
              </div>
            ))}
          </div>
        </div>

        {/* Dots Indicator - Mobile */}
        {totalCards > 1 && (
          <div className="flex justify-center gap-2 mt-4">
            {children.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-[10px] w-[10px] rounded-full transition-all ${
                  index === currentIndex
                    ? 'bg-purple-500'
                    : 'bg-white/30'
                }`}
                style={{
                    minHeight: '10px',
                    minWidth: '10px',
                }}
                aria-label={`Go to card ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Desktop Grid View */}
      <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {children.map((child, index) => (
          <div key={index}>
            {child}
          </div>
        ))}
      </div>
    </div>
  );
};

